/**
 * Revenue Dashboard - Platform revenue analytics and metrics
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, change, changeType, icon: Icon, prefix = '' }) => {
  const isPositive = changeType === 'positive';
  
  return (
    <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-soft-silver text-sm">{title}</p>
          <p className="text-3xl font-bold text-cloud-white mt-2">{prefix}{value}</p>
          {change && (
            <div className={cn(
              "flex items-center gap-1 mt-2 text-sm",
              isPositive ? "text-cool-mint" : "text-signal-red"
            )}>
              {isPositive ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              <span>{change}% from last month</span>
            </div>
          )}
        </div>
        <div className="p-3 bg-electric-ice-blue/10 rounded-xl">
          <Icon className="w-6 h-6 text-electric-ice-blue" />
        </div>
      </div>
    </div>
  );
};

// Revenue Source Row
const RevenueSourceRow = ({ name, amount, percentage, trend }) => (
  <div className="flex items-center justify-between p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
    <div className="flex items-center gap-3">
      <div className="w-2 h-2 rounded-full bg-electric-ice-blue" />
      <span className="text-cloud-white font-medium">{name}</span>
    </div>
    <div className="flex items-center gap-6">
      <span className="text-soft-silver">{percentage}%</span>
      <span className="text-cloud-white font-mono">${amount.toLocaleString()}</span>
      <div className={cn(
        "flex items-center gap-1 text-sm",
        trend >= 0 ? "text-cool-mint" : "text-signal-red"
      )}>
        {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span>{Math.abs(trend)}%</span>
      </div>
    </div>
  </div>
);

export const Revenue = () => {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);

  const fetchRevenueData = useCallback(async () => {
    setLoading(true);
    try {
      // In a full implementation, this would fetch from a revenue API
      // For now, we'll compute metrics from campaigns and subscriptions
      const res = await fetch(`${API_URL}/api/campaigns/stats`, {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      
      if (res.ok) {
        const stats = await res.json();
        // Calculate total revenue from campaign spend
        const totalSpent = stats?.total_spent || 0;
        
        setRevenueData({
          totalRevenue: totalSpent || 12450,
          mrr: Math.round((totalSpent || 12450) * 0.8),
          activeSubscriptions: stats?.total_campaigns || 45,
          avgRevenuePerOrg: Math.round((totalSpent || 12450) / (stats?.total_campaigns || 45)),
          sources: [
            { name: 'Ad Campaigns', amount: Math.round((totalSpent || 8000) * 0.6), percentage: 60, trend: 12 },
            { name: 'Premium Subscriptions', amount: Math.round((totalSpent || 3000) * 0.25), percentage: 25, trend: 8 },
            { name: 'Event Ticketing', amount: Math.round((totalSpent || 1200) * 0.10), percentage: 10, trend: -3 },
            { name: 'Other', amount: Math.round((totalSpent || 250) * 0.05), percentage: 5, trend: 2 },
          ],
        });
      } else {
        // Default demo data
        setRevenueData({
          totalRevenue: 12450,
          mrr: 9960,
          activeSubscriptions: 45,
          avgRevenuePerOrg: 276,
          sources: [
            { name: 'Ad Campaigns', amount: 7470, percentage: 60, trend: 12 },
            { name: 'Premium Subscriptions', amount: 3112, percentage: 25, trend: 8 },
            { name: 'Event Ticketing', amount: 1245, percentage: 10, trend: -3 },
            { name: 'Other', amount: 623, percentage: 5, trend: 2 },
          ],
        });
      }
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      // Use demo data on error
      setRevenueData({
        totalRevenue: 12450,
        mrr: 9960,
        activeSubscriptions: 45,
        avgRevenuePerOrg: 276,
        sources: [
          { name: 'Ad Campaigns', amount: 7470, percentage: 60, trend: 12 },
          { name: 'Premium Subscriptions', amount: 3112, percentage: 25, trend: 8 },
          { name: 'Event Ticketing', amount: 1245, percentage: 10, trend: -3 },
          { name: 'Other', amount: 623, percentage: 5, trend: 2 },
        ],
      });
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchRevenueData();
  }, [fetchRevenueData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenue-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Revenue</h1>
          <p className="text-soft-silver text-sm mt-1">Platform revenue analytics and metrics</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRevenueData}
          className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={revenueData?.totalRevenue?.toLocaleString()}
          change={15}
          changeType="positive"
          icon={DollarSign}
          prefix="$"
        />
        <StatCard
          title="Monthly Recurring"
          value={revenueData?.mrr?.toLocaleString()}
          change={8}
          changeType="positive"
          icon={TrendingUp}
          prefix="$"
        />
        <StatCard
          title="Active Subscriptions"
          value={revenueData?.activeSubscriptions}
          change={12}
          changeType="positive"
          icon={Users}
        />
        <StatCard
          title="Avg. Revenue/Org"
          value={revenueData?.avgRevenuePerOrg}
          change={-3}
          changeType="negative"
          icon={Calendar}
          prefix="$"
        />
      </div>

      {/* Revenue Sources */}
      <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] p-6">
        <h2 className="text-lg font-semibold text-cloud-white mb-4">Revenue by Source</h2>
        <div className="space-y-3">
          {revenueData?.sources?.map((source, index) => (
            <RevenueSourceRow key={index} {...source} />
          ))}
        </div>
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] p-6">
        <h2 className="text-lg font-semibold text-cloud-white mb-4">Revenue Trend</h2>
        <div className="h-64 flex items-center justify-center border border-dashed border-white/[0.08] rounded-xl">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 text-muted-text mx-auto mb-2" />
            <p className="text-soft-silver">Revenue chart visualization</p>
            <p className="text-xs text-muted-text mt-1">Interactive charts coming with analytics integration</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revenue;
